import type { PortableTextBlock, PortableTextComponentProps } from '@portabletext/react';
import cn from 'classnames';
import type { ImageWithMetadata} from '../components/SanityImage';
import SanityImage, { imageWithMetadataGroq } from '../components/SanityImage';
import useDataAttribute from '../infrastructure/DataAttributeContext';
import type { Keyed } from '../types';
import SimpleRichText from './SimpleRichText';
import { simpleRichTextGroq } from './simpleRichTextGroq';
import * as style from './Textblock.module.css';

export const textblockGroq = /* groq */ `
{
    _key,
    _type,
    color,
    showTitle,
    showImage,
    reference -> {
      "title": coalesce(title, name, question),
      "text": coalesce(simpleRichText, description, body, answer)[] ${simpleRichTextGroq},
      image ${imageWithMetadataGroq}
    }
}
`;

export interface TextblockProps {
    color?: 'nothing' | 'yellow' | 'grey' | 'beige' | 'pink';
    showTitle?: 'nothing' | 'h2' | 'h3' | 'h4';
    showImage?: 'nothing' | 'left' | 'right';
    reference?: {
        title?: string;
        text?: PortableTextBlock[];
        image?: ImageWithMetadata;
    };
}

const ImageWidths = [172, 172 * 2, 172 * 3];

export function Textblock({ value }: PortableTextComponentProps<Keyed<TextblockProps>>) {
    const attr = useDataAttribute();
    if (value.color === 'nothing' || !value.color) {
        return (
            <>
                <Title title={value.reference?.title} showTitle={value.showTitle} />
                {(!value.showImage || value.showImage !== 'nothing') && (
                    <figure className={cn({ float: value.showImage }, value.showImage)}>
                        <SanityImage image={value.reference?.image} sizes="277px" widths={[277, 277 * 2, 277 * 3]} lazy />
                    </figure>
                )}
                <SimpleRichText value={value.reference?.text ?? []} />
            </>
        );
    } else {
        return (
            <div
                className={cn(style.textboxWithColor, style[value.color], value.showImage && style[value.showImage])}
                data-sanity={attr?.([{ _key: value._key }])}
            >
                {(!value.showImage || value.showImage !== 'nothing') && (
                    <SanityImage image={value.reference?.image} sizes="172px" widths={ImageWidths} lazy />
                )}
                <div>
                    <Title title={value.reference?.title} showTitle={value.showTitle} />
                    <SimpleRichText value={value.reference?.text ?? []} />
                </div>
            </div>
        );
    }
}

function Title({ title, showTitle }: { title?: string; showTitle?: 'nothing' | 'h2' | 'h3' | 'h4' }) {
    if (!title) return null;

    switch (showTitle) {
        case 'h2':
            return <h2>{title}</h2>;
        case 'h3':
            return <h3>{title}</h3>;
        case 'h4':
            return <h4>{title}</h4>;
        case 'nothing':
        default:
            return null;
    }
}
